import React, {useState} from 'react';
import {Box, Stack, TextField, Tooltip, Typography} from "@mui/material"
import {LockOutlined} from "@mui/icons-material"
import {ProjectJson} from "../../api/json/ProjectJson"
import AsyncButton from "../button/AsyncButton"
import {ProjectRequests} from "../../api/requests/ProjectRequests"
import useSaveShortcut from "../../hooks/useSaveShortcut"
import {useProjectData} from "../../hooks/useProjectData"
import {useToasts} from "../../hooks/useToasts"
import ProjectTypeIcon from "../projects/ProjectTypeIcon"
import {resolveNameForProjectType} from "../../api/enum/ProjectType"

interface ProjectSettingsFormProps {
    project: ProjectJson
    didUpdate: (project: ProjectJson) => void
}

function ProjectSettingsForm({project, didUpdate}: ProjectSettingsFormProps) {
    const {setProject} = useProjectData()

    const [name, setName] = useState(project.name)
    const [description, setDescription] = useState(project.description)

    const [saveInProgress, setSaveInProgress] = useState(false)
    const toasts = useToasts()

    const unsavedChanges = (
        project.name !== name
        || project.description !== description
    )

    const disableSave = !unsavedChanges || name.length === 0 || description.length === 0

    const handleSave = () => {
        if (disableSave) return

        setSaveInProgress(true)
        ProjectRequests.update(project.id, name, description)
            .then(updated => didUpdate(updated))
            .catch(toasts.showError)
            .finally(() => setSaveInProgress(false))
    }
    useSaveShortcut(handleSave)

    return (
        <Stack spacing={2}>
            <Box sx={{pb: 1.5}}>
                <Typography variant="caption" color="text.secondary" sx={{display: "block"}}>Type</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <ProjectTypeIcon type={project.type} color="action" fontSize="small"/>
                    <Typography>{resolveNameForProjectType(project.type)}</Typography>
                    <Tooltip title="Set at creation and can't be changed">
                        <LockOutlined sx={{fontSize: 16, color: "text.disabled"}}/>
                    </Tooltip>
                </Stack>
            </Box>

            <TextField label="Name"
                       value={name}
                       onChange={e => {
                           setName(e.target.value.slice(0, 60))
                       }}
            />
            <TextField label="Description"
                       value={description}
                       multiline
                       helperText="This provides important context for various features."
                       onChange={e => setDescription(e.target.value.slice(0, 300))}
            />

            <AsyncButton waiting={saveInProgress}
                         disabled={disableSave}
                         onClick={handleSave}>
                Save
            </AsyncButton>
        </Stack>
    );
}

export default ProjectSettingsForm;
